import React from "react";
import { motion } from "framer-motion";
import { Lock, Check, PlayCircle, BookOpen as StoryIcon, LayoutGrid, HelpCircle, Mic, RotateCcw, Coffee } from "lucide-react";

const DAY_META = {
    1: { label: "Poem", icon: PlayCircle },
    2: { label: "Story Book", icon: StoryIcon },
    3: { label: "Word Cards", icon: LayoutGrid },
    4: { label: "Quiz", icon: HelpCircle },
    5: { label: "Sing & Rec", icon: Mic },
    6: { label: "Rehearse", icon: RotateCcw },
    7: { label: "Off", icon: Coffee },
};

// A row of standalone day cards — no connector line. Each card is its own
// rounded rectangle: current day gets a solid green border + tinted fill and
// its icon, a completed day gets the same green treatment with a checkmark,
// and a locked day is plain/grey with just a lock icon — no countdown text
// cluttering the card itself. Tapping a locked day is what shows the "time
// left to unlock" detail, via the onLockedClick dialog. All 7 days (5 real
// lessons + Rehearse + a contentless rest day) come straight from the
// backend's own schedule — nothing here computes an unlock time itself.
//
// `marquee`: the landing page teaser (MicroOnboarding) embeds this in a
// narrow card next to the video, where 7 equal-width flex-shrunk cards
// truncate almost every label ("Story Bo…", "Sing & R…") — fine once a
// learner already knows the week's shape, not fine as someone's very first
// look at it. Marquee mode drops the equal-width squeeze for fixed-width
// cards sized to fit each label in full, and drifts the (doubled, for a
// seamless loop) strip slowly right-to-left, instead of leaving it to a
// not-obviously-scrollable overflow the visitor has to discover on their
// own. 26s per loop is slow enough that a deliberate tap still lands fine.
// PoemPlayer's real, interactive timeline is untouched — marquee defaults
// off.
export default function WeekTimeline({ release, selectedDay, onSelectDay, onLockedClick, marquee = false, completedOverride }) {
    if (!release) return null;
    const currentDay = release.current_day;

    const cards = [1, 2, 3, 4, 5, 6, 7].map((day) => {
        const d = release.days[String(day)];
        const meta = DAY_META[day];
        // Days 1-4 have "day < currentDay" as their completion signal, but
        // current_day's fallback never advances past 5 — so Day 5 itself
        // would stay stuck showing as "current" (the mic icon) forever,
        // even long after it was actually recorded. Day 5, Rehearse, and
        // Off all use release.week_completed instead: once the whole
        // week's 5 real lessons are done, there's nothing left to do on
        // any of them, so all three read as complete.
        const defaultCompleted = day <= 4 ? (d.unlocked && day < currentDay) : (d.unlocked && !!release.week_completed);
        // Class Lesson mode, client bug report 2026-08-27: this whole
        // `release` is computed off just ONE representative student —
        // exactly right for Days 1-3 (mark-class-progress batches them
        // identically for everyone present), but Day 4/5 are completed
        // individually at home, so one child finishing them made the
        // representative's own release look "done" and painted the
        // WHOLE WEEK green here even while other real students in the
        // class hadn't finished at all. completedOverride lets
        // PoemPlayer.jsx substitute the CLASS's actual all-present-
        // students-done status for just these two days (and everything
        // that depends on them) instead of the representative's own.
        const completed = completedOverride && completedOverride[day] !== undefined
            ? (d.unlocked && completedOverride[day])
            : defaultCompleted;
        const isCurrent = d.unlocked && day === currentDay && !completed;
        const locked = !d.unlocked;
        return {
            day,
            globalDay: d.global_day || day,
            meta,
            locked,
            completed,
            current: isCurrent,
            onClick: () => (locked ? onLockedClick?.(day, meta.label, d.unlocks_at, d.global_day, d.source) : onSelectDay?.(day)),
        };
    });

    return (
        <div className="rounded-2xl bg-white border border-honeySoft/70 p-3 sm:p-4" data-testid="week-timeline">
            <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-mocha mb-2">This week&apos;s lessons</p>
            {marquee ? (
                <div
                    className="overflow-hidden"
                    style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}
                >
                    <motion.div
                        className="flex gap-2 w-max"
                        // 0% -> -50%: drifts right-to-left (the doubled list's
                        // first copy scrolls off the left edge as the second
                        // copy scrolls in from the right).
                        animate={{ x: ["0%", "-50%"] }}
                        transition={{ duration: 26, ease: "linear", repeat: Infinity }}
                        data-testid="week-timeline-scroll"
                    >
                        {[...cards, ...cards].map((c, i) => (
                            <DayCard key={`${c.day}-${i}`} {...c} wide />
                        ))}
                    </motion.div>
                </div>
            ) : (
                // Bug fix, 2026-09-01: on a narrow phone the 7 day-cards
                // (each with a min-width) genuinely don't fit — confirmed
                // live this row already scrolls fine (overflow-x-auto), but
                // with nothing at all hinting that Day 6/7 exist just off
                // the right edge, a learner has no reason to think to swipe
                // and can easily believe the week just stops at Day 5. The
                // same fade-mask the marquee variant above already uses
                // makes the cut-off edge read as "more this way" instead of
                // a hard, final-looking boundary — harmless on a wide
                // screen where every card already fits, since the mask only
                // ever touches the outermost few pixels.
                <div
                    className="overflow-x-auto"
                    style={{ maskImage: "linear-gradient(to right, black 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, black 92%, transparent)" }}
                >
                    <div className="flex flex-nowrap gap-1 sm:gap-2" data-testid="week-timeline-scroll">
                        {cards.map((c) => <DayCard key={c.day} {...c} />)}
                    </div>
                </div>
            )}
        </div>
    );
}

function DayCard({ day, globalDay, meta, locked, completed, current, onClick, wide = false }) {
    const accessible = completed || current;

    return (
        <motion.button
            type="button"
            onClick={onClick}
            whileHover={!locked ? { y: -2 } : undefined}
            whileTap={{ scale: 0.96 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            data-testid={`week-timeline-day-${day}`}
            aria-disabled={locked}
            className={`relative flex flex-col items-center text-center gap-0.5 rounded-xl border px-1 py-2 transition-all duration-200 ${
                wide ? "w-[92px] sm:w-[104px] shrink-0" : "flex-1 basis-0 min-w-[52px]"
            } ${
                current
                    ? "bg-brightGreen/10 border-brightGreen shadow-sm"
                    : completed
                    ? "bg-brightGreen/5 border-brightGreen/50"
                    : "bg-cream border-transparent"
            }`}
        >
            <span className={`flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full ${
                current ? "bg-brightGreen text-white" : completed ? "bg-brightGreen/20 text-brightGreen" : "text-lockedText"
            }`}>
                {locked ? (
                    <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.6} />
                ) : completed ? (
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={3} />
                ) : (
                    <meta.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.6} />
                )}
            </span>
            <span className={`text-[8px] sm:text-[9px] font-extrabold uppercase tracking-wide leading-none mt-0.5 ${accessible ? "text-charcoal" : "text-mocha"}`}>
                Day {globalDay}
            </span>
            <span className={`text-[9px] sm:text-[10px] font-bold leading-tight max-w-full ${wide ? "whitespace-nowrap" : "truncate"} ${accessible ? "text-deepGreen" : "text-mocha"}`}>
                {meta.label}
            </span>
        </motion.button>
    );
}
